import { useEffect, useState } from "react";
import { ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";

// Real client logos with a slogan + one-line "what they do" — matches the
// existing Kyndryl/IBM style for every entry.
const CLIENTS = [
  {
    key: "bnp",
    name: "BNP Paribas Cardif",
    href: "https://www.bnpparibascardif.com",
    src: "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/jgs9d74c_image.png",
    padding: "8%",
    slogan: "The insurer for a changing world",
    desc: "Global insurance & financial protection",
  },
  {
    key: "pitcher",
    name: "Pitcher",
    href: "https://pitcher.com",
    src: "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/tqidvtgd_download.png",
    padding: "16%",
    slogan: "Sales enablement, reimagined",
    desc: "Mobile-first sales enablement platform",
  },
  {
    key: "tf",
    name: "Thermo Fisher Scientific",
    href: "https://www.thermofisher.com",
    src: "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/mc829t5k_thermo-fisher-scientific-1.jpg",
    padding: "8%",
    slogan: "The world leader in serving science",
    desc: "Life sciences & analytical instruments",
  },
  {
    key: "pwc",
    name: "PwC",
    href: "https://www.pwc.com",
    src: "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/siidrkv7_image.png",
    padding: "14%",
    slogan: "Build trust. Deliver sustained outcomes",
    desc: "Audit · Advisory · Consulting",
  },
  {
    key: "ibm",
    name: "IBM",
    href: "https://www.ibm.com",
    src: "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/b8dxq06j_image.png",
    padding: "18%",
    slogan: "Let's create",
    desc: "Enterprise IT · Cloud · AI",
  },
  {
    key: "kyndryl",
    name: "Kyndryl",
    href: "https://www.kyndryl.com",
    src: "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/nytiajug_download.png",
    padding: "18%",
    slogan: "The Heart of Progress",
    desc: "Mission-critical IT services",
  },
  {
    key: "db",
    name: "Deutsche Börse Group",
    href: "https://www.deutsche-boerse.com",
    src: "https://customer-assets.emergentagent.com/job_impact-platform-demo/artifacts/h7znv3m3_image-67bf278ee4434-1740580750.png",
    padding: "8%",
    slogan: "Designing trusted markets",
    desc: "Capital markets infrastructure",
  },
];

const VISIBLE = 4;
const INTERVAL_MS = 3000;

export const References = () => {
  const { t } = useLanguage();
  const [startIdx, setStartIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setStartIdx((prev) => (prev + 1) % CLIENTS.length);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused]);

  const visibleClients = Array.from({ length: VISIBLE }).map(
    (_, i) => CLIENTS[(startIdx + i) % CLIENTS.length],
  );

  return (
    <section
      id="references"
      data-testid="references-section"
      className="bg-[#181B24] py-16 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="text-center mb-10 md:mb-12">
          <p className="font-body italic text-white/60 text-base mb-4">
            {t.references.eyebrow}
          </p>
          <h2
            data-testid="references-title"
            className="font-heading uppercase font-bold leading-none tracking-[0.02em] text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl"
          >
            {t.references.titleMain}{" "}
            <span className="gradient-text">{t.references.titleAccent}</span>
          </h2>
        </div>

        <div
          data-testid="references-carousel"
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <button
            data-testid="references-prev"
            onClick={() =>
              setStartIdx((p) => (p - 1 + CLIENTS.length) % CLIENTS.length)
            }
            aria-label="Previous clients"
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 lg:-translate-x-4 z-10 w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-white text-[#181B24] items-center justify-center hover:bg-[#FFE21A] hover:scale-105 transition-all shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
          >
            <ChevronLeft size={24} strokeWidth={2.5} />
          </button>

          <button
            data-testid="references-next"
            onClick={() => setStartIdx((p) => (p + 1) % CLIENTS.length)}
            aria-label="Next clients"
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 lg:translate-x-4 z-10 w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-white text-[#181B24] items-center justify-center hover:bg-[#FFE21A] hover:scale-105 transition-all shadow-[0_10px_30px_rgba(0,0,0,0.25)]"
          >
            <ChevronRight size={24} strokeWidth={2.5} />
          </button>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {visibleClients.map((c) => (
              <a
                key={c.key}
                data-testid={`reference-card-${c.key}`}
                href={c.href}
                target="_blank"
                rel="noopener noreferrer"
                className="relative bg-white rounded-2xl overflow-hidden group hover-lift flex flex-col animate-fade-in-up"
              >
                {/* Logo — uniform fixed box, object-contain so the logo scales
                    to fill the box while preserving aspect ratio. No per-logo
                    percentage padding (broke layout on mobile). */}
                <div className="h-36 sm:h-32 md:h-36 flex items-center justify-center px-8 py-6">
                  <img
                    src={c.src}
                    alt={c.name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                </div>

                {/* Slogan + description */}
                <div className="px-5 pb-5 pt-2 flex-1 flex flex-col items-center text-center">
                  <p
                    className="font-heading font-bold uppercase tracking-[0.12em] text-[10px] md:text-[11px] text-[#181B24]/85 leading-snug"
                    data-testid={`reference-slogan-${c.key}`}
                  >
                    {c.slogan}
                  </p>
                  <p
                    className="mt-1.5 font-body text-[11px] md:text-xs text-[#181B24]/55 leading-snug"
                    data-testid={`reference-desc-${c.key}`}
                  >
                    {c.desc}
                  </p>
                </div>

                {/* Yellow name bar */}
                <div className="gradient-bg px-5 py-3 flex items-center justify-between mt-auto">
                  <span className="font-heading font-bold uppercase tracking-[0.08em] text-[#181B24] text-sm truncate">
                    {c.name}
                  </span>
                  <ExternalLink
                    size={16}
                    className="text-[#181B24] opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0"
                  />
                </div>
              </a>
            ))}
          </div>

          <div
            data-testid="references-dots"
            className="mt-10 flex items-center justify-center gap-2"
          >
            {CLIENTS.map((c, i) => (
              <button
                key={c.key}
                onClick={() => setStartIdx(i)}
                aria-label={`Show clients starting from ${c.name}`}
                data-testid={`references-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === startIdx ? "w-10 bg-[#FFE21A]" : "w-1.5 bg-white/30"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default References;
